import {
  Activity,
  Building2,
  Shapes,
  ClipboardList,
  MessageSquareWarning,
  Receipt,
  MessageSquare,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { PERMS } from '../../rbac/permissions';

export interface NavItem {
  label: string;
  to: string;
  perm: bigint;
}

export interface NavGroup {
  key: string;
  label: string;
  Icon: LucideIcon;
  items: NavItem[];
}

// Structure mirrors V1's grouped sidebar (spec §7.6). Slice 2 adds a 'users'
// group, Slice 3 a 'pricing' group; nothing else here changes.
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    key: 'tenants',
    label: 'Tenant Management',
    Icon: Building2,
    items: [
      { label: 'All Tenants', to: '/tenants', perm: PERMS.TENANTS_READ },
      // Task 14: the Slice-0 seam this comment refers to is `visibleGroups`
      // above -- appending one item here is the whole change; the sidebar
      // and the command palette both pick it up with nothing else touched.
      { label: 'Module Matrix', to: '/modules/matrix', perm: PERMS.ENTITLEMENTS_READ },
    ],
  },
  {
    key: 'business',
    label: 'Business Manager',
    Icon: Shapes,
    items: [{ label: 'Business Types', to: '/business-types', perm: PERMS.BUSINESS_TYPES_READ }],
  },
  {
    key: 'users',
    label: 'User Management',
    Icon: Users,
    items: [
      { label: 'People', to: '/users', perm: PERMS.USERS_READ },
      { label: 'Staff', to: '/staff', perm: PERMS.STAFF_READ },
      { label: 'Roles', to: '/admin-roles', perm: PERMS.ROLES_MANAGE },
    ],
  },
  {
    key: 'onboarding',
    label: 'Onboarding',
    Icon: ClipboardList,
    items: [
      { label: 'Queue', to: '/onboarding', perm: PERMS.ONBOARDING_VIEW },
      { label: 'Funnel', to: '/onboarding/funnel', perm: PERMS.ONBOARDING_VIEW },
    ],
  },
  {
    key: 'assistant',
    label: 'AI Assistant',
    Icon: MessageSquareWarning,
    items: [
      { label: 'Escalations', to: '/assistant', perm: PERMS.ASSISTANT_REVIEW },
      { label: 'Knowledge', to: '/assistant/knowledge', perm: PERMS.ASSISTANT_CURATE },
      { label: 'Analytics', to: '/assistant/analytics', perm: PERMS.ASSISTANT_REVIEW },
    ],
  },
  {
    key: 'billing',
    label: 'Billing',
    Icon: Receipt,
    items: [{ label: 'Messaging billing', to: '/cost-plus', perm: PERMS.COST_PLUS_READ }],
  },
  {
    key: 'support',
    label: 'Support',
    Icon: MessageSquare,
    items: [{ label: 'Feedback', to: '/feedback', perm: PERMS.FEEDBACK_REVIEW }],
  },
  // E-3 L3 (P2-H01): the Worker page; its sections are discovered per file.
  {
    key: 'ops',
    label: 'Operations',
    Icon: Activity,
    items: [{ label: 'Worker', to: '/worker', perm: PERMS.WORKER_READ }],
  },
];

/**
 * Filters the model down to what this admin may see. A group whose every item
 * was filtered out disappears rather than rendering an empty expandable row.
 * Returns fresh objects — NAV_GROUPS is module-level and must stay pristine.
 */
export function visibleGroups(
  groups: readonly NavGroup[],
  hasPerm: (bits: bigint) => boolean,
): NavGroup[] {
  return groups
    .map((g) => ({ ...g, items: g.items.filter((i) => hasPerm(i.perm)) }))
    .filter((g) => g.items.length > 0);
}

/**
 * Which group owns `pathname`, by longest-prefix match so a nested route
 * ('/onboarding/funnel') resolves to its own item rather than to a shorter
 * sibling's. Used to auto-expand the active group.
 */
export function groupKeyForPath(groups: readonly NavGroup[], pathname: string): string | null {
  let bestKey: string | null = null;
  let bestLen = -1;
  for (const g of groups) {
    for (const i of g.items) {
      if ((pathname === i.to || pathname.startsWith(`${i.to}/`)) && i.to.length > bestLen) {
        bestLen = i.to.length;
        bestKey = g.key;
      }
    }
  }
  return bestKey;
}
